import { useState } from "react";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import {
  createQueue,
  updateQueue,
  uploadQueueMedia,
  previewQueueChange,
  applyQueueChange,
  validatePromptApi,
} from "../../services/queue.api";

// D8.1.4c-2 — Orquestación de guardado del modal de colas: payload, guardar,
// preview/apply de cambios, guardar horarios y validación del proveedor fallback.
// Recibe los datos del form como input. onClose se pasa EN LA LLAMADA (evita la
// dependencia circular con handleClose de index).
export default function useQueueSave({
  queueId,
  selectedPrompt,
  selectedFlowBuilder,
  schedules,
  attachment,
  keywordGroups,
  queueOptionsRef,
  setSchedules,
  setTab,
}) {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [pendingQueueData, setPendingQueueData] = useState(null);
  const [fallbackValidatingApi, setFallbackValidatingApi] = useState(false);
  const [fallbackApiValidated, setFallbackApiValidated] = useState(false);

  const getHasChatbotOptions = () => {
    return queueOptionsRef.current &&
      queueOptionsRef.current.getOptionsCount &&
      queueOptionsRef.current.getOptionsCount() > 0;
  };

  // M5: quitar greetingEnabled del payload API; vaciar saludo si desactivado
  const buildQueuePayload = (vals) => {
    const { greetingEnabled, ...rest } = vals;
    return {
      ...rest,
      greetingMessage: greetingEnabled ? String(vals.greetingMessage || "").trim() : "",
    };
  };

  const handleSaveQueue = async (values, onClose) => {
    try {
      // Validación mutuamente exclusiva: CHATBOT | PROMPT | INTEGRACIÓN | FLOWBUILDER
      const hasPrompt = selectedPrompt && selectedPrompt !== null && selectedPrompt !== "";
      const hasIntegration = values.integrationId && values.integrationId !== null && values.integrationId !== "";
      const hasFlowBuilder = selectedFlowBuilder && selectedFlowBuilder !== null && selectedFlowBuilder !== "";

      const chatbotOptionsCount = getHasChatbotOptions() ?
        (queueOptionsRef.current.getOptionsCount ? queueOptionsRef.current.getOptionsCount() : 0) : 0;
      const hasChatbot = chatbotOptionsCount > 0;

      const activeCount = [hasPrompt, hasIntegration, hasChatbot, hasFlowBuilder].filter(Boolean).length;

      if (activeCount > 1) {
        toast.error("❌ Error: Solo puedes seleccionar UNA opción: Chatbot, Prompt, Integración o FlowBuilder. No puedes seleccionar múltiples opciones simultáneamente.");
        return;
      }

      let savedQueueId = queueId;
      const payloadValues = buildQueuePayload(values);

      // Si estamos editando, verificar si hay cambios que afecten tickets
      if (queueId) {
        try {
          const { data: preview } = await previewQueueChange(queueId, {
            promptId: selectedPrompt ? selectedPrompt : null,
            integrationId: payloadValues.integrationId || null,
            hasOptions: keywordGroups.length > 0 // Si hay opciones de chatbot
          });

          // Si hay cambios que requieren confirmación, mostrar modal
          if (preview.requiresConfirmation) {
            setPendingQueueData({
              values,
              savedQueueId,
              attachment
            });
            setPreviewData(preview);
            setPreviewModalOpen(true);
            return; // Detener aquí, el guardado se hará después de confirmar
          }
        } catch (error) {
          console.error("❌ Error al obtener preview:", error);
          // Si falla el preview, continuar con el guardado normal
        }
      }

      // GUARDAR DEPARTAMENTO
      if (queueId) {
        const putPayload = {
          ...payloadValues, schedules, promptId: selectedPrompt ? selectedPrompt : null,
          flowBuilderId: selectedFlowBuilder ? selectedFlowBuilder : null,
          contingencyQueueId: payloadValues.contingencyQueueId ? Number(payloadValues.contingencyQueueId) : null,
          contingencyMessage: payloadValues.contingencyMessage || null,
          aiFallbackEnabled: payloadValues.aiFallbackEnabled || false,
          aiFallbackProvider: payloadValues.aiFallbackEnabled ? (payloadValues.aiFallbackProvider || null) : null,
          aiFallbackApiKey: payloadValues.aiFallbackEnabled ? (payloadValues.aiFallbackApiKey || null) : null
        };
        console.log("PAYLOAD FRONTEND (put):", putPayload);
        await updateQueue(queueId, putPayload);
      } else {
        const postPayload = {
          ...payloadValues, schedules, promptId: selectedPrompt ? selectedPrompt : null,
          flowBuilderId: selectedFlowBuilder ? selectedFlowBuilder : null,
          contingencyQueueId: payloadValues.contingencyQueueId ? Number(payloadValues.contingencyQueueId) : null,
          contingencyMessage: payloadValues.contingencyMessage || null,
          aiFallbackEnabled: payloadValues.aiFallbackEnabled || false,
          aiFallbackProvider: payloadValues.aiFallbackEnabled ? (payloadValues.aiFallbackProvider || null) : null,
          aiFallbackApiKey: payloadValues.aiFallbackEnabled ? (payloadValues.aiFallbackApiKey || null) : null
        };
        console.log("PAYLOAD FRONTEND (post):", postPayload);
        const { data } = await createQueue(postPayload);
        savedQueueId = data.id;
      }

      // GUARDAR ARCHIVO ADJUNTO
      if (attachment != null) {
        const formData = new FormData();
        formData.append("file", attachment);
        await uploadQueueMedia(savedQueueId, formData);
      }

      // GUARDAR TODAS LAS OPCIONES TEMPORALES
      if (queueOptionsRef.current && queueOptionsRef.current.saveAllOptions) {
        await queueOptionsRef.current.saveAllOptions(savedQueueId);
      }

      toast.success(i18n.t("queues.success"));
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  // Confirmar y aplicar cambios después del preview
  const handleConfirmConfigChange = async (onClose) => {
    try {
      if (!pendingQueueData || !queueId) {
        throw new Error("No hay datos pendientes para guardar");
      }

      const { values, savedQueueId, attachment: pendingAttachment } = pendingQueueData;

      // Aplicar cambios a tickets afectados
      await applyQueueChange(queueId, {
        changeType: previewData.changeType,
        newConfig: {
          promptId: selectedPrompt ? selectedPrompt : null,
          integrationId: values.integrationId || null
        }
      });

      // Guardar configuración del departamento
      const payloadValues = buildQueuePayload(values);
      await updateQueue(queueId, {
        ...payloadValues, schedules, promptId: selectedPrompt ? selectedPrompt : null,
        flowBuilderId: selectedFlowBuilder ? selectedFlowBuilder : null,
        contingencyQueueId: payloadValues.contingencyQueueId ? Number(payloadValues.contingencyQueueId) : null,
        contingencyMessage: payloadValues.contingencyMessage || null
      });

      // GUARDAR ARCHIVO ADJUNTO
      if (pendingAttachment != null) {
        const formData = new FormData();
        formData.append("file", pendingAttachment);
        await uploadQueueMedia(savedQueueId, formData);
      }

      // GUARDAR TODAS LAS OPCIONES TEMPORALES
      if (queueOptionsRef.current && queueOptionsRef.current.saveAllOptions) {
        await queueOptionsRef.current.saveAllOptions(savedQueueId);
      }

      toast.success(i18n.t("queues.success"));

      // Cerrar modal de preview
      setPreviewModalOpen(false);
      setPendingQueueData(null);
      setPreviewData(null);

      // Cerrar modal principal
      onClose();

      return { success: true, updatedTickets: previewData.affectedTickets, errors: 0 };
    } catch (error) {
      console.error("❌ Error al aplicar cambios:", error);
      throw error;
    }
  };

  const handleSaveSchedules = async (values) => {
    toast.success(i18n.t("queueModal.schedules.saveSuccess"));
    setSchedules(values);
    setTab(0);
  };

  const handleValidateFallbackApi = async (provider, apiKey) => {
    if (!provider) {
      toast.error("Selecciona primero el proveedor de respaldo");
      return;
    }
    if (!apiKey || !apiKey.trim() || apiKey.trim().length < 20) {
      toast.error("Ingresa una API Key válida (mínimo 20 caracteres)");
      return;
    }
    try {
      setFallbackValidatingApi(true);
      setFallbackApiValidated(false);
      const { data } = await validatePromptApi({
        apiKey: apiKey.trim(),
        provider
      });
      if (data.success) {
        setFallbackApiValidated(true);
        toast.success(`✅ ${data.message || "Conexión verificada correctamente"}`);
      } else {
        setFallbackApiValidated(false);
        toast.error(data.message || "Error al validar la conexión");
      }
    } catch (err) {
      setFallbackApiValidated(false);
      toast.error(err.response?.data?.message || err.message || "Error al validar la conexión");
    } finally {
      setFallbackValidatingApi(false);
    }
  };

  return {
    previewModalOpen,
    setPreviewModalOpen,
    previewData,
    setPreviewData,
    pendingQueueData,
    setPendingQueueData,
    fallbackValidatingApi,
    setFallbackValidatingApi,
    fallbackApiValidated,
    setFallbackApiValidated,
    handleSaveQueue,
    handleConfirmConfigChange,
    handleSaveSchedules,
    handleValidateFallbackApi,
  };
}
